import { useState } from "react";
import { FiCheck, FiX } from "react-icons/fi";
import { Button } from "newui/components/Button";
import Modal from "newui/components/ui/Modal/Modal";
import TextBoxHTML from "newui/components/TextBoxHTML";
import type {
	ReviewClarification,
	TriageStatus,
	DeclineReason,
} from "../../../../types/clarification-review.type";
import { ChangeTrackingView } from "./ChangeTrackingView";
import { DeclineReasonModal } from "./DeclineReasonModal";
import { TriageStatusBadge } from "./TriageStatusBadge";

interface ReviewClarificationModalProps {
	isOpen: boolean;
	clarification: ReviewClarification | null;
	onClose: () => void;
	onTriage?: (
		clarificationId: number,
		status: TriageStatus,
		declineReason?: string,
		declineCategory?: DeclineReason,
	) => void;
	isTriaging?: boolean;
}

export const ReviewClarificationModal: React.FC<ReviewClarificationModalProps> = ({
	isOpen,
	clarification,
	onClose,
	onTriage,
	isTriaging = false,
}) => {
	const [showDeclineModal, setShowDeclineModal] = useState(false);

	if (!clarification) {
		return null;
	}

	const hasBeenEdited =
		clarification.original_clarification_text !== null &&
		clarification.original_clarification_text !== clarification.clarification_text;

	const isPending = clarification.triage_status === null;
	const canTriage = isPending && typeof onTriage === "function";

	const handleAccept = () => {
		if (!onTriage) {
			return;
		}
		onTriage(clarification.id, "accepted");
	};

	const handleDeclineConfirm = (reason: string, category: DeclineReason) => {
		if (!onTriage) {
			return;
		}
		onTriage(clarification.id, "declined", reason, category);
		setShowDeclineModal(false);
	};

	const customFooter = canTriage ? (
		<div className="flex w-full items-center justify-between">
			<div className="flex items-center gap-2">
				<Button
					btntype="primary"
					size="sm"
					width="auto"
					onClick={handleAccept}
					disabled={isTriaging}
					className="!bg-green-600 hover:!bg-green-700 !border-green-600"
					data-testid="modal-accept-btn"
				>
					<FiCheck size={14} className="mr-1" />
					Accept
				</Button>
				<Button
					btntype="secondary"
					size="sm"
					width="auto"
					onClick={() => setShowDeclineModal(true)}
					disabled={isTriaging}
					className="!border-red-200 !text-red-600 hover:!bg-red-50"
					data-testid="modal-decline-btn"
				>
					<FiX size={14} className="mr-1" />
					Decline
				</Button>
			</div>
			<Button
				btntype="secondary"
				size="sm"
				width="auto"
				onClick={onClose}
				data-testid="modal-close-btn"
			>
				Close
			</Button>
		</div>
	) : undefined;

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			size="xl"
			title={`Clarification #${clarification.reference_number}`}
			description={`${clarification.supplier_name}`}
			modalType="standard"
			iconType="info"
			closeOnEsc
			closeOnOverlayClick
			customFooter={customFooter}
			secondaryButton={
				!customFooter
					? {
						onClick: onClose,
						text: "Close",
						size: "sm",
						width: "auto",
					}
					: undefined
			}
		>
			<div className="space-y-4" data-testid="review-clarification-modal">
				<div className="flex items-center gap-3">
					<TriageStatusBadge status={clarification.triage_status} />
					{clarification.created_by && (
						<span className="text-xs text-gray-400">
							Proposed by {clarification.created_by.name}
							{clarification.created_by.company_name && ` (${clarification.created_by.company_name})`}
						</span>
					)}
				</div>

				<div
					className="rounded-lg border border-gray-200 bg-gray-50 p-3"
					data-testid="relates-to-card"
				>
					<p className="mb-1 text-xs font-medium uppercase tracking-wider text-gray-500">
						Relates to
					</p>
					<p className="text-sm text-gray-700">
						{clarification.relates_to || "—"}
					</p>
				</div>

				{hasBeenEdited ? (
					<ChangeTrackingView clarification={clarification} />
				) : (
					<div className="rounded-lg border border-gray-200 p-4" data-testid="clarification-text-view">
						<p className="mb-1 text-xs font-medium uppercase tracking-wider text-gray-400">
							Clarification Text
						</p>
						<TextBoxHTML
							className="text-sm text-gray-700"
							html={clarification.clarification_text}
						/>
					</div>
				)}

				{clarification.decline_reason && (
					<div className="rounded-lg border border-red-100 bg-red-50 p-3">
						<p className="text-xs font-medium text-red-600">Decline Reason</p>
						<p className="mt-0.5 text-sm text-red-700">{clarification.decline_reason}</p>
					</div>
				)}
			</div>

			{showDeclineModal && (
				<DeclineReasonModal
					isOpen={showDeclineModal}
					count={1}
					onClose={() => setShowDeclineModal(false)}
					onConfirm={handleDeclineConfirm}
					isLoading={isTriaging}
				/>
			)}
		</Modal>
	);
};
